import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { parseISO, addHours, format, addDays } from 'date-fns';

import { FaGlobe, FaTrashAlt } from 'react-icons/fa';
import { isString } from 'lodash';
import { optionsHorasCadastroAgenda } from '../../../../utils/funcoes';
import Button from '../../../../components/Button';
import Toggle from '../../../../components/Exame/Toggle';
import Input from '../../../../components/Input';
import SelectSync from '../../../../components/SelectSync';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { useToast } from '../../../../hooks/toast';
import Loading from '../../../../components/Loading';
import api from '../../../../services/api';
import getResponseErrors from '../../../../utils/getResponseErrors';

// import { Container } from './styles';

interface IListagemDiaNaoAgendar {
  DiaCodigo: number;
  DiaConCodigo: number;
  DiaData: Date | string;
  DiaHoraIni: string;
  DiaHoraFim: string;
  DiaObs: string;
  exibeExcluir: boolean;
}

interface IDiasNaoAgendar extends IListagemDiaNaoAgendar {
  DiaConCodigo: number;
  DiaTodo?: string;
}

interface IFormataDiaNaoAgendar {
  diasNaoAgendar: IListagemDiaNaoAgendar[];
  conCodigo: number;
}

function formataDiaNaoAgendar({
  diasNaoAgendar,
  conCodigo,
}: IFormataDiaNaoAgendar): IListagemDiaNaoAgendar[] {
  const listagemDiasNaoAgendarFormatada = diasNaoAgendar.map(diaNaoAgendar => {
    const Dia = isString(diaNaoAgendar.DiaData)
      ? parseISO(diaNaoAgendar.DiaData)
      : diaNaoAgendar.DiaData;

    const exibeExcluir =
      (diaNaoAgendar.DiaConCodigo !== 0 && conCodigo !== 0) ||
      (diaNaoAgendar.DiaConCodigo === 0 && conCodigo === 0);

    return {
      ...diaNaoAgendar,
      DiaData: format(Dia, 'dd/MM/yyyy'),
      exibeExcluir,
    };
  });

  return listagemDiasNaoAgendarFormatada;
}

type Props = {
  conCodigo: number;
};
const DiaNaoAgendarCadastro: React.FC<Props> = ({ conCodigo }) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [listagemDiaNaoAgendar, setListagemDiaNaoAgendar] = useState<
    IListagemDiaNaoAgendar[]
  >([]);
  const formRefdiasNaoAgendar = useRef<FormHandles>(null);
  const optionsHora = useMemo(() => {
    return optionsHorasCadastroAgenda();
  }, []);

  useEffect(() => {
    async function buscarDias(): Promise<void> {
      try {
        setLoading(true);

        const response = await api.get<IListagemDiaNaoAgendar[]>(
          `/dianaoagendar/${conCodigo}`,
        );

        const listagemDiasNaoAgendarApiFormatada = formataDiaNaoAgendar({
          diasNaoAgendar: response.data,
          conCodigo,
        });

        setListagemDiaNaoAgendar(listagemDiasNaoAgendarApiFormatada);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar dias não agendar',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    buscarDias();
  }, [conCodigo, addToast]);

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  const addDiaNaoAgendar = useCallback(
    async (dadosDiaNaoAgendar: IDiasNaoAgendar) => {
      setLoading(true);
      try {
        const response = await api.post<IDiasNaoAgendar>(
          '/dianaoagendar',
          dadosDiaNaoAgendar,
        );

        const {
          DiaCodigo,
          DiaConCodigo,
          DiaData,
          DiaHoraIni,
          DiaHoraFim,
          DiaObs,
        } = response.data;
        const retornDiaNaoAgendar = formataDiaNaoAgendar({
          diasNaoAgendar: [
            {
              DiaCodigo,
              DiaConCodigo,
              DiaData,
              DiaHoraIni,
              DiaHoraFim,
              DiaObs,
              exibeExcluir: true,
            },
          ],
          conCodigo,
        });

        setListagemDiaNaoAgendar(state => {
          return state
            ? [retornDiaNaoAgendar[0]].concat(
                state.filter(
                  dia => dia.DiaCodigo !== retornDiaNaoAgendar[0].DiaCodigo,
                ),
              )
            : [retornDiaNaoAgendar[0]];
        });

        addToast({ title: 'Dia não agendar salvo', type: 'success' });
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível adicionar dia não agendar',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, conCodigo],
  );

  const handleSubmitDiasNaoAgendar = useCallback(
    async (dados: IDiasNaoAgendar) => {
      try {
        formRefdiasNaoAgendar.current?.setErrors({});
        const schema = Yup.object().shape({
          DiaData: Yup.date()
            .min(
              addDays(new Date(), -1),
              'A data precisa ser maior que a data de hoje',
            )
            .required('informe a data inicial'),
          DiaTodo: Yup.boolean(),
          DiaHoraIni: Yup.string().when('DiaTodo', {
            is: !true,
            then: Yup.string().required('informe a hora inicial'),
          }),
          DiaHoraFim: Yup.string().when('DiaTodo', {
            is: !true,
            then: Yup.string().required('informe a hora final'),
          }),
          DiaObs: Yup.string().required('informe a observação'),
        });

        const diaTodo = dados.DiaTodo ? dados.DiaTodo.toString() : 'false';
        const diasNaoAgendarForm = {
          ...dados,
          DiaTodo: diaTodo === 'true',
        };

        await schema.validate(diasNaoAgendarForm, {
          abortEarly: false,
        });

        const dadosEnvio: IDiasNaoAgendar = {
          ...diasNaoAgendarForm,
          DiaObs: diasNaoAgendarForm.DiaObs.toUpperCase(),
          DiaConCodigo: conCodigo,
          DiaData: isString(diasNaoAgendarForm.DiaData)
            ? addHours(parseISO(diasNaoAgendarForm.DiaData), 10)
            : addHours(diasNaoAgendarForm.DiaData, 10),
          DiaHoraIni: diasNaoAgendarForm.DiaTodo
            ? '00:00'
            : diasNaoAgendarForm.DiaHoraIni,
          DiaHoraFim: diasNaoAgendarForm.DiaTodo
            ? '00:00'
            : diasNaoAgendarForm.DiaHoraFim,
          DiaTodo: undefined,
        };

        await addDiaNaoAgendar(dadosEnvio);
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRefdiasNaoAgendar.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Erro ao salvar dia não agendar',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, addDiaNaoAgendar, conCodigo],
  );

  const handleEnviar = useCallback(() => {
    formRefdiasNaoAgendar.current?.submitForm();
  }, []);

  const deleteDiaNaoAgendar = useCallback(
    async (codigo: number) => {
      setLoading(true);
      try {
        await api.delete(`/dianaoagendar/${codigo}`);
        setListagemDiaNaoAgendar(DiasState =>
          DiasState?.filter(dia => dia.DiaCodigo !== codigo),
        );
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível deletar registro',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  return (
    <>
      {loading && <Loading />}
      <hr className="mt-1 mb-3" />

      <Row noGutters>
        <Col md={12}>
          <Form
            ref={formRefdiasNaoAgendar}
            initialData={{ DiaTodo: false }}
            onSubmit={handleSubmitDiasNaoAgendar}
          >
            <Row className="small">
              <Col md={3} className="form-group">
                <Input
                  name="DiaData"
                  label="Dia para não atendimento"
                  placeholder="Selecione uma data"
                  type="date"
                />
              </Col>
              <Col md={1} className="form-group">
                <Toggle
                  nome="DiaTodo"
                  classSpam={{
                    paddingRight: '10px',
                    marginBottom: '7px',
                    fontWeight: 'bold',
                  }}
                  label="Dia Todo"
                  isDisabled={false}
                />
              </Col>
              <Col md={2} className="form-group">
                <SelectSync
                  name="DiaHoraIni"
                  options={optionsHora}
                  handleSelectChange={handleOnChangeVazio}
                  placeholder="00:00"
                  label=" ou De"
                />
              </Col>
              <Col md={2} className="form-group">
                <SelectSync
                  name="DiaHoraFim"
                  options={optionsHora}
                  handleSelectChange={handleOnChangeVazio}
                  placeholder="00:00"
                  label="Até"
                />
              </Col>

              <Col md={2} className="form-group">
                <Input
                  name="DiaObs"
                  label="Observação"
                  placeholder="Ex. Viagem, Médico..."
                  type="text"
                  maxLength={100}
                />
              </Col>
              <Col md={2} className="form-group">
                <Button
                  type="button"
                  variant="success-outline"
                  className="mt-3"
                  disabled={loading}
                  onClick={() => handleEnviar()}
                >
                  {loading ? 'adicionando...' : 'Adicionar'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <Row>
        <Col md={12} lg={12}>
          <div>
            <table
              className="table table-hover table-sm"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Data</th>
                  <th scope="col">Inicio</th>
                  <th scope="col">Inicio</th>
                  <th scope="col">Obs</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {loading && (
                  <tr>
                    <td colSpan={5} className="text-center">
                      <b>carregando...</b>
                    </td>
                  </tr>
                )}
                {!loading && listagemDiaNaoAgendar.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center">
                      <b>Nenhum dia adicionado.</b>
                    </td>
                  </tr>
                )}
                {!loading &&
                  listagemDiaNaoAgendar.length > 0 &&
                  listagemDiaNaoAgendar.map(lista => (
                    <tr
                      key={`diasNaoAgendar-${lista.DiaCodigo}`}
                      style={{
                        fontSize: 'Small',
                        fontWeight:
                          lista.DiaConCodigo === 0 ? 'bold' : 'normal',
                      }}
                    >
                      <td style={{ width: '65px' }}>{lista.DiaData}</td>
                      <td style={{ width: '55px' }}>{lista.DiaHoraIni}</td>
                      <td style={{ width: '55px' }}>{lista.DiaHoraFim}</td>
                      <td>{lista.DiaObs}</td>
                      <td className="text-center" style={{ width: '40px' }}>
                        {lista.DiaConCodigo === 0 && !lista.exibeExcluir && (
                          <FaGlobe size={18} />
                        )}
                        {lista.exibeExcluir && (
                          <Button
                            type="button"
                            variant="secondary-outline"
                            className="py-0"
                            title={`${lista.DiaCodigo}`}
                            onClick={() => {
                              deleteDiaNaoAgendar(lista.DiaCodigo);
                            }}
                          >
                            <FaTrashAlt />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
    </>
  );
};

export default DiaNaoAgendarCadastro;
